import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Scales, FileMagnifyingGlass, Lightning, Lock, Warning, MagnifyingGlass,
  ArrowRight, Check, Anchor, Stack, Gauge, Export, CurrencyCircleDollar, Translate, ShieldCheck,
} from "@phosphor-icons/react";
import { LCShieldLogo } from "@/components/LCShieldLogo";
import { PRIVACY_BADGES } from "@/components/Privacy";
import { Seo } from "@/components/Seo";
import { SandboxDemo } from "@/components/SandboxDemo";
import { RoiCalculator } from "@/components/RoiCalculator";
import { ComparisonTable } from "@/components/ComparisonTable";
import { api } from "@/lib/api";
import { track, getRefEmail } from "@/lib/track";
import { CURRENCIES, FX, planDisplay } from "@/lib/fx";
import { ApiKeyGuideModal } from "@/components/ApiKeyGuideModal";
import { HeroPromoVideo } from "@/components/HeroPromoVideo";
import { useI18n, LANGS } from "@/i18n";
import { FooterLinks, AgreeNote } from "@/components/FooterLinks";

const SHOW_TEST_HINTS = process.env.REACT_APP_SHOW_TEST_HINTS === "true";

const HERO_IMG =
  "https://images.pexels.com/photos/12530455/pexels-photo-12530455.jpeg?auto=compress&cs=tinysrgb&fm=webp&w=1280";
const HK_IMG =
  "https://images.unsplash.com/photo-1605745341112-85968b19335b?fm=webp&w=1280&q=70&fit=crop";
const DATA_IMG =
  "https://images.unsplash.com/photo-1644088379091-d574269d422f?fm=webp&w=1280&q=70&fit=crop";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const Overline = ({ children, className = "" }) => (
  <span className={`font-mono text-[11px] uppercase tracking-[0.28em] ${className}`}>{children}</span>
);

// Robust fallback so the pricing grid always renders even if /api/pricing is slow or fails.
// Prices/lookup keys mirror the backend PLANS; display text comes from i18n by plan id.
const FALLBACK_PLANS = [
  { id: "single", cadence: "one-time", highlight: false,
    prices: { usd: { amount: 15, symbol: "$", lookup_key: "lc_single_audit_usd" }, hkd: { amount: 99, symbol: "HK$", lookup_key: "lc_single_audit" } } },
  { id: "professional", cadence: "month", highlight: true,
    prices: { usd: { amount: 199, symbol: "$", lookup_key: "lc_pro_monthly_usd" }, hkd: { amount: 1550, symbol: "HK$", lookup_key: "lc_pro_monthly" } } },
  { id: "firm", cadence: "month", highlight: false,
    prices: { usd: { amount: 599, symbol: "$", lookup_key: "lc_firm_monthly_usd" }, hkd: { amount: 4680, symbol: "HK$", lookup_key: "lc_firm_monthly" } } },
  { id: "solo", cadence: "one-time", highlight: false,
    prices: { usd: { amount: 499, symbol: "$", lookup_key: "lc_solo_lifetime_usd" }, hkd: { amount: 3880, symbol: "HK$", lookup_key: "lc_solo_lifetime" } } },
];

export default function Landing() {
  const nav = useNavigate();
  const { t, lang, setLang } = useI18n();
  const [plans, setPlans] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [currency, setCurrency] = useState(() => localStorage.getItem("lc_ccy") || "usd");
  const [keyGuideOpen, setKeyGuideOpen] = useState(false);

  const changeCurrency = (c) => { setCurrency(c); localStorage.setItem("lc_ccy", c); };

  const scrollToPricing = () =>
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" });

  const goDemo = () => nav("/workspace?demo=mt700");
  const goSample = () => nav("/workspace?demo=mt700&tour=1");

  // Keep the mobile sticky CTA hidden until the visitor scrolls past the hero,
  // so it never covers the hero buttons on first load (360x740 / 360x800).
  const [showMobileBar, setShowMobileBar] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowMobileBar(window.scrollY > window.innerHeight * 0.75);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/pricing").then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  // Cold-outreach deep link: /?demo=sample auto-launches the interactive sample audit.
  // Optional &src= (e.g. cold_email_v1) is logged to Campaign Analytics.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("demo") === "sample") {
      const src = (params.get("src") || "").trim().slice(0, 60) || "direct";
      track("sample_deeplink_opened", { src });
      nav("/workspace?demo=mt700", { replace: true });
    }
  }, [nav]);

  // Auto-scroll to a hash target (e.g. /#pricing, /#demo) after mount / hash change.
  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.replace("#", "");
      if (!id) return;
      const tries = [0, 200, 500];
      tries.forEach((d) =>
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), d)
      );
    };
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, []);

  const checkout = async (lookup_key, id) => {
    setLoadingPlan(id);
    track("paywall_modal_opened", { plan: id });
    try {
      const ref_email = getRefEmail();
      const r = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin, ref_email });
      track("checkout_initiated", { plan: id });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoadingPlan(null);
    }
  };

  const LangSwitcher = () => (
    <div className="flex items-center rounded-sm border border-white/15 p-0.5" data-testid="lang-switcher">
      {LANGS.map((l) => (
        <button key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLang(l.code)}
          className={`rounded-sm px-2 py-1 text-[11px] font-semibold transition-colors duration-200 ${
            lang === l.code ? "bg-lc-sky text-lc-navy" : "text-lc-fog hover:text-white"
          }`}>{l.label}</button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-lc-navy text-white font-sans antialiased overflow-x-hidden pb-20 md:pb-0">
      <Seo
        path="/"
        title="LC Shield | LC Discrepancy Check Against UCP 600 & ISBP 821"
        description="Pre-audit your Letter of Credit presentation against UCP 600 and ISBP 821 (2023). Instantly scan SWIFT MT700 drafts and trade documents, flag discrepancies, and draft an amendment request before presenting to your issuing bank."
      />
      {/* NAV */}
      <nav className="fixed top-0 inset-x-0 z-50 border-b border-white/10 bg-lc-navy/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
            <LCShieldLogo className="text-lc-sky" size={26} />
            <span className="font-serif text-xl tracking-tight whitespace-nowrap">LC Shield</span>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="hidden lg:flex items-center gap-6 text-sm text-lc-fog">
              <a href="#how" className="hover:text-white transition-colors duration-200">{t("nav.how")}</a>
              <a href="#features" className="hover:text-white transition-colors duration-200">{t("nav.engine")}</a>
              <a href="#pricing" className="hover:text-white transition-colors duration-200">{t("nav.pricing")}</a>
              <button onClick={() => nav("/resources")} data-testid="nav-resources" className="hover:text-white transition-colors duration-200">{t("nav.resources")}</button>
            </div>
            <button
              data-testid="nav-open-workspace"
              onClick={() => nav("/workspace")}
              className="group inline-flex items-center gap-2 rounded-sm bg-lc-sky px-4 py-2 text-sm font-semibold text-lc-navy hover:bg-white transition-colors duration-200"
            >
              {t("nav.open")}
              <ArrowRight weight="bold" size={15} className="group-hover:translate-x-0.5 transition-transform duration-200" />
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="relative min-h-screen flex items-center pt-16">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Loaded container ship at a trade port" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-lc-navy via-lc-navy/92 to-lc-navy/40" />
          <div className="absolute inset-0 grain opacity-60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 w-full grid lg:grid-cols-12 gap-10 py-24">
          <motion.div initial="hidden" animate="show" className="lg:col-span-7">
            <motion.div variants={fade} custom={0}>
              <Overline className="text-lc-sky">{t("hero.overline")}</Overline>
            </motion.div>
            <motion.h1
              variants={fade} custom={1}
              className={`mt-5 font-serif font-semibold tracking-tight leading-[1.05] ${
                lang === "zh-TW" || lang === "zh-CN"
                  ? "text-2xl sm:text-4xl lg:text-[2.75rem]"
                  : "text-4xl sm:text-5xl lg:text-6xl"
              }`}
            >
              {lang === "zh-TW" || lang === "zh-CN" ? (
                <>
                  <span className="block break-keep text-balance md:whitespace-nowrap">{t("hero.title1")}</span>
                  <span className="block break-keep text-balance md:whitespace-nowrap">{t("hero.title2")}</span>
                </>
              ) : (
                <>{t("hero.title1")}<br />{t("hero.title2")}</>
              )}
            </motion.h1>
            <motion.p variants={fade} custom={2} className="mt-6 max-w-xl text-lg text-lc-fog leading-relaxed">
              {t("hero.sub")}
            </motion.p>
            <motion.div variants={fade} custom={3} className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-open-workspace"
                onClick={() => nav("/workspace")}
                className="group inline-flex items-center gap-2 rounded-sm bg-lc-sky px-6 py-3.5 text-sm font-semibold text-lc-navy hover:bg-white transition-colors duration-200"
              >
                {t("hero.ctaPrimary")}
                <ArrowRight weight="bold" size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
              </button>
              <button
                data-testid="hero-try-sample"
                onClick={goSample}
                className="group inline-flex items-center gap-2 rounded-sm border border-lc-sky/40 bg-lc-sky/10 px-6 py-3.5 text-sm font-semibold text-white hover:bg-lc-sky/20 transition-colors duration-200"
              >
                <Lightning weight="fill" size={16} className="text-lc-sky" /> {t("hero.ctaSample")}
              </button>
            </motion.div>
            <motion.p variants={fade} custom={4} data-testid="hero-no-card"
              className="mt-4 flex items-center gap-2 text-xs text-lc-fog">
              <Check weight="bold" size={14} className="text-lc-ok shrink-0" /> {t("hero.noCard")}
            </motion.p>
            <motion.div variants={fade} custom={5} className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-lc-fog">
              <span className="inline-flex items-center gap-2"><Lock weight="duotone" size={16} className="text-lc-ok" /> {t("hero.badge1")}</span>
              <span className="inline-flex items-center gap-2"><Scales weight="duotone" size={16} className="text-lc-sky" /> {t("hero.badge2")}</span>
              <span className="inline-flex items-center gap-2"><Export weight="duotone" size={16} className="text-lc-sky" /> {t("hero.badge3")}</span>
            </motion.div>
          </motion.div>

          {/* Right column: 30-second promo master (beside headline on desktop, stacks below CTA on mobile) */}
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 flex items-center justify-center lg:justify-end"
          >
            <HeroPromoVideo />
          </motion.div>
        </div>
      </header>

      {/* AUDIT FEE GUARANTEE — risk reversal */}
      <section className="border-b border-white/10 bg-lc-slate/40" data-testid="guarantee-band">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col gap-4 rounded-md border border-lc-ok/30 bg-lc-ok/5 p-6 sm:flex-row sm:items-center">
            <div className="flex shrink-0 items-center gap-3">
              <ShieldCheck weight="duotone" size={40} className="text-lc-ok" />
              <span className="font-serif text-lg text-white sm:hidden">{t("guarantee.badge")}</span>
            </div>
            <div className="min-w-0">
              <p className="hidden font-serif text-lg text-white sm:block">
                <span className="text-lc-ok">{t("guarantee.badge")}:</span> <span className="text-lc-fog text-base">{t("guarantee.body")}</span>
              </p>
              <p className="text-sm leading-relaxed text-lc-fog sm:hidden">{t("guarantee.body")}</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                <p className="flex flex-wrap items-center gap-x-2 font-mono text-[11px] uppercase tracking-wider text-lc-ok/80">
                  <Check weight="bold" size={13} /> {t("guarantee.micro")}
                </p>
                <Link to="/guarantee" data-testid="guarantee-link"
                  className="group inline-flex items-center gap-1 text-[12px] font-semibold text-lc-ok hover:text-white transition-colors duration-200">
                  {t("guarantee.readTerms")} <ArrowRight weight="bold" size={12} className="group-hover:translate-x-0.5 transition-transform duration-200" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SandboxDemo onUnlock={goDemo} />

      {/* STAT BAND */}
      <section className="border-y border-white/10 bg-lc-slate/40">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
          {[
            { k: "65-80%", v: t("stat.v1") },
            { k: "USD 50–150", v: t("stat.v2") },
            { k: "5–15 days", v: t("stat.v3") },
          ].map((s, i) => (
            <motion.div
              key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
              className="py-10 sm:px-8 first:pl-0"
            >
              <div className="font-serif text-4xl text-lc-sky">{s.k}</div>
              <p className="mt-2 text-sm text-lc-fog leading-relaxed">{s.v}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-24">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
          <Overline className="text-lc-sky">{t("work.overline")}</Overline>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight">{t("work.title")}</h2>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-3 gap-8">
          {[
            { icon: Stack, t: t("work.s1t"), d: t("work.s1d") },
            { icon: FileMagnifyingGlass, t: t("work.s2t"), d: t("work.s2d") },
            { icon: Export, t: t("work.s3t"), d: t("work.s3d") },
          ].map((s, i) => (
            <motion.div
              key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
              className="rounded-sm border border-white/10 bg-lc-slate/30 p-8 hover:border-lc-sky/40 transition-colors duration-200"
            >
              <s.icon weight="duotone" size={34} className="text-lc-sky" />
              <h3 className="mt-5 font-serif text-xl">{s.t}</h3>
              <p className="mt-3 text-sm text-lc-fog leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="max-w-7xl mx-auto px-6 pb-24">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
          <Overline className="text-lc-sky">{t("feat.overline")}</Overline>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight max-w-3xl [text-wrap:balance]">
            {t("feat.title")}
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-6 gap-6">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
            className="md:col-span-4 relative overflow-hidden rounded-sm border border-white/10 bg-lc-slate/30 p-8 min-h-[280px]">
            <img src={DATA_IMG} alt="" loading="lazy" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-20" />
            <div className="relative">
              <Scales weight="duotone" size={34} className="text-lc-sky" />
              <h3 className="mt-5 font-serif text-2xl">{t("feat.c1t")}</h3>
              <p className="mt-3 max-w-md text-sm text-lc-fog leading-relaxed">
                {t("feat.c1d")}
              </p>
              <div className="mt-6 flex flex-wrap gap-2 font-mono text-[11px] text-lc-fog">
                {["Art. 14(c)", "Art. 18(b)", "Art. 20", "Art. 28", "ISBP 821 (2023)"].map((a) => (
                  <span key={a} className="rounded-sm border border-white/15 px-2 py-1">{a}</span>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={1}
            className="md:col-span-2 rounded-sm border border-white/10 bg-lc-slate/30 p-8">
            <Lock weight="duotone" size={34} className="text-lc-ok" />
            <h3 className="mt-5 font-serif text-xl">{t("feat.c2t")}</h3>
            <p className="mt-3 text-sm text-lc-fog leading-relaxed">
              {t("feat.c2d")}
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={2}
            className="md:col-span-2 rounded-sm border border-white/10 bg-lc-slate/30 p-8">
            <Gauge weight="duotone" size={34} className="text-lc-sky" />
            <h3 className="mt-5 font-serif text-xl">{t("feat.c3t")}</h3>
            <p className="mt-3 text-sm text-lc-fog leading-relaxed">
              {t("feat.c3d")}
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={3}
            className="md:col-span-4 relative overflow-hidden rounded-sm border border-white/10 bg-lc-slate/30 p-8">
            <img src={HK_IMG} alt="Cargo vessel on a global trade route" loading="lazy" className="absolute right-0 top-0 h-full w-2/5 object-cover opacity-25" />
            <div className="relative">
              <Anchor weight="duotone" size={34} className="text-lc-sky" />
              <h3 className="mt-5 font-serif text-2xl">{t("feat.c4t")}</h3>
              <p className="mt-3 max-w-md text-sm text-lc-fog leading-relaxed">
                {t("feat.c4d")}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ENTERPRISE PRIVACY GUARANTEE */}
      <section className="mx-auto max-w-6xl px-6 py-16" data-testid="landing-privacy-section">
        <div className="rounded-sm border border-white/10 bg-lc-slate/30 p-8 md:p-10">
          <div className="flex items-center gap-2">
            <ShieldCheck weight="duotone" size={22} className="text-lc-ok" />
            <h2 className="font-serif text-2xl md:text-3xl">{t("shield.title")}</h2>
          </div>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {PRIVACY_BADGES.map((b, i) => (
              <div key={i} data-testid={`landing-privacy-badge-${i}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-lc-ok/10">
                  <b.icon weight="duotone" size={22} className="text-lc-ok" />
                </div>
                <h3 className="mt-4 font-serif text-lg text-white">{t(b.t)}</h3>
                <p className="mt-2 text-sm text-lc-fog leading-relaxed">{t(b.d)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <ComparisonTable />

      <RoiCalculator onCta={scrollToPricing} currency={currency} plans={plans} />

      <section id="pricing" className="border-t border-white/10 bg-lc-slate/20">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
            <Overline className="text-lc-sky">{t("pricing.overline")}</Overline>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight">{t("pricing.title")}</h2>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <div className="flex items-center rounded-sm border border-white/15 p-0.5" data-testid="currency-toggle">
                {CURRENCIES.map((c) => (
                  <button key={c} data-testid={`currency-${c}`} onClick={() => changeCurrency(c)}
                    className={`rounded-sm px-3 py-1.5 text-xs font-semibold uppercase transition-colors duration-200 ${
                      currency === c ? "bg-lc-sky text-lc-navy" : "text-lc-fog hover:text-white"
                    }`}>{FX[c].label}</button>
                ))}
              </div>
              {SHOW_TEST_HINTS && <p className="text-lc-fog text-sm">{t("pricing.sub")}</p>}
            </div>
          </motion.div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {(plans.length ? plans : FALLBACK_PLANS).map((p, i) => {
              const price = planDisplay(p, currency);
              const features = t(`plan.${p.id}.features`);
              const featList = Array.isArray(features) ? features : [];
              const quotaNote = t(`plan.${p.id}.quotaNote`);
              const hasNote = typeof quotaNote === "string" && !quotaNote.startsWith("plan.");
              return (
              <motion.div
                key={p.id} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
                data-testid={`plan-card-${p.id}`}
                className={`relative rounded-sm border p-7 flex flex-col ${
                  p.highlight ? "border-lc-sky bg-lc-navy ring-1 ring-lc-sky/40" : "border-white/10 bg-lc-slate/30"
                }`}
              >
                <span className={`inline-flex w-fit items-center rounded-sm px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${
                  p.highlight ? "bg-lc-sky/15 text-lc-sky" : "bg-white/5 text-lc-fog"
                }`} data-testid={`plan-badge-${p.id}`}>
                  {t(`plan.${p.id}.badge`)}
                </span>
                <h3 className="mt-3 font-serif text-2xl">{t(`plan.${p.id}.name`)}</h3>
                <p className="mt-1 text-sm text-lc-fog leading-relaxed min-h-[2.5rem]">{t(`plan.${p.id}.tag`)}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="font-mono text-sm text-lc-fog">{price.symbol}</span>
                  <span data-testid={`plan-price-${p.id}`} className="font-serif text-4xl">{price.amount.toLocaleString()}</span>
                  <span className="text-sm text-lc-fog">/{p.cadence === "month" ? t("pricing.perMonth") : t("pricing.oneTime")}</span>
                </div>
                {/* Quota cap highlight */}
                <div data-testid={`plan-quota-${p.id}`}
                  className={`mt-4 rounded-sm border px-3 py-2.5 ${
                    p.highlight ? "border-lc-sky/40 bg-lc-sky/10" : "border-white/10 bg-white/5"
                  }`}>
                  <p className="text-sm font-semibold text-white leading-snug">{t(`plan.${p.id}.quota`)}</p>
                  {hasNote && <p className="mt-0.5 text-[11px] text-lc-fog">{quotaNote}</p>}
                </div>
                <ul className="mt-5 space-y-3 flex-1">
                  {featList.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-lc-fog">
                      <Check weight="bold" size={16} className="mt-0.5 shrink-0 text-lc-sky" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {p.id === "solo" && (
                  <button type="button" onClick={() => setKeyGuideOpen(true)} data-testid="solo-apikey-guide-trigger"
                    className="mt-6 -mb-1 text-center text-xs font-semibold text-lc-sky hover:underline whitespace-nowrap">
                    {t("apikey.trigger")}
                  </button>
                )}
                <button
                  data-testid={`plan-buy-${p.id}`}
                  disabled={loadingPlan === p.id}
                  onClick={() => checkout(price.lookup_key, p.id)}
                  className={`mt-6 inline-flex items-center justify-center gap-2 rounded-sm px-5 py-3 text-sm font-semibold transition-colors duration-200 disabled:opacity-60 ${
                    p.highlight
                      ? "bg-lc-sky text-lc-navy hover:bg-white"
                      : "border border-white/20 text-white hover:bg-white/5"
                  }`}
                >
                  <CurrencyCircleDollar weight="bold" size={16} />
                  {loadingPlan === p.id ? t("pricing.starting") : t(`plan.${p.id}.cta`)}
                </button>
                {price.billedInUsd && (
                  <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-wider text-lc-fog" data-testid={`plan-billed-usd-${p.id}`}>
                    {t("pricing.billedUsd")}
                  </p>
                )}
              </motion.div>
              );
            })}
          </div>

          <AgreeNote prefix={t("pricing.agreePrefix")} variant="dark" className="mt-8 text-center" testid="pricing-agree-note" />

          <p className="mt-4 text-center text-xs text-lc-fog mx-auto max-w-none lg:whitespace-nowrap">
            {t("pricing.footnote")}
          </p>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <div className="rounded-sm border border-lc-warn/30 bg-lc-warn/5 p-6 flex gap-4" data-testid="landing-disclaimer">
          <Warning weight="duotone" size={26} className="shrink-0 text-lc-warn" />
          <p className="text-xs leading-relaxed text-lc-fog">{t("disclaimer.global")}</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-lc-fog">
          <div className="flex items-center gap-2">
            <LCShieldLogo className="text-lc-sky" size={20} />
            <span className="font-serif text-lg text-white">LC Shield</span>
          </div>
          <FooterLinks variant="dark" />
        </div>
      </footer>

      {/* bottom spacer so the mobile sticky bar never covers page content at 360px */}
      <div className="h-24 md:hidden" aria-hidden="true" />

      {/* STICKY MOBILE CTA — revealed only after scrolling past the hero */}
      <div data-testid="mobile-cta-bar"
        className={`fixed bottom-0 inset-x-0 z-50 md:hidden border-t border-white/10 bg-lc-navy/95 backdrop-blur-md px-4 py-3 transition-transform duration-300 ${showMobileBar ? "translate-y-0" : "translate-y-full"}`}>
        <button data-testid="mobile-start-scan-btn"
          onClick={() => nav("/workspace")}
          className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-lc-sky px-5 py-3.5 text-sm font-semibold text-lc-navy active:bg-white transition-colors duration-200">
          <MagnifyingGlass weight="bold" size={16} /> {t("mobile.startScan")}
        </button>
      </div>
      <ApiKeyGuideModal open={keyGuideOpen} onClose={() => setKeyGuideOpen(false)} />
    </div>
  );
}
